import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField, InputAdornment, Checkbox, CircularProgress, Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    ArrowLeft, ArrowRight, ArrowDownWideNarrow, ArrowUpNarrowWide, Trash2, X, User,
    Clock, Users, Award, CheckCircle2, Mail, ExternalLink, Search, Check,
    CalendarDays, CalendarRange, ChevronDown,
} from 'lucide-react';
import Calling from '../../api/Calling';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';
import MainButton from '../../components/ul/MainButton';
import DeleteButton from '../../components/ul/DeleteButton';
import NotFound from '../../components/ul/NotFound';
import TableSkeleton from '../../components/ul/TableSkeleton';
import getISTDate from '../../components/getISTDate';

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    // Brand the open-picker (calendar) icon button
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup.
// All component keys & style slots below are verified to exist in @mui/x-date-pickers@9.6.0.
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        // Popup surface (paper) — rounded + soft premium shadow
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        // Calendar header: month/year label + nav area
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: {
                    color: '#374151',
                    '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND },
                },
            },
        },
        // Nav arrows (prev / next month)
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: {
                    color: '#6b7280',
                    borderRadius: 10,
                    '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND },
                },
            },
        },
        // Weekday labels (Mo, Tu, …)
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: {
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                },
            },
        },
        // Individual day cells
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        // Year selection grid
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
            },
        },
        // Month selection grid
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
            },
        },
    },
});

// Shared slots/slotProps for both pickers — branded lucide icons (verified slot names for v9.6)
const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];
const checkboxSx = {
    color: '#9ca3af',
    p: 0.5,
    '&.Mui-checked': { color: '#34C759' },
    '&.MuiCheckbox-indeterminate': { color: '#34C759' },
};

const ReferralPlanHistory = () => {
    const [loader, setLoader] = useState(true);
    const [historyData, setHistoryData] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate);
    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));
    const [statusFilter, setStatusFilter] = useState('');
    const [selectState, setSelectState] = useState('today');
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit] = useState(10);

    // Stats - calculated from current filtered data
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        expired: 0,
    });

    const fetchHistoryData = useCallback(async (page = 1) => {
        setLoader(true);
        try {
            const response = await Calling.getReferralPlanHistory(
                page,
                limit,
                startDate,
                endDate,
                statusFilter,
                sortOrder,
                search,
                ''
            );

            if (response?.status) {
                const data = response?.data || [];
                setHistoryData(data);
                setCurrentPage(response?.page || 1);
                setTotalPages(response?.totalPage || 1);
                setTotalRecords(response?.totalRecord || 0);

                // Calculate stats from current page data
                const activeCount = data.filter(item => item.status === 'active').length;
                const expiredCount = data.filter(item => item.status === 'expired').length;

                setStats({
                    total: response?.totalRecord || 0,
                    active: activeCount,
                    expired: expiredCount,
                });
            } else {
                toast.error(response?.message || 'Failed to fetch referral plan history');
                setHistoryData([]);
                setStats({ total: 0, active: 0, expired: 0 });
            }
        } catch (error) {
            toast.error('Failed to fetch referral plan history');
            setHistoryData([]);
            setStats({ total: 0, active: 0, expired: 0 });
        } finally {
            setLoader(false);
        }
    }, [limit, startDate, endDate, statusFilter, sortOrder, search]);

    useEffect(() => {
        fetchHistoryData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, statusFilter, sortOrder, search]);

    // Keep the picker drafts in sync whenever the committed range changes (quick buttons / apply)
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowModal(false);
                setSelectedPlan(null);
            }
        };

        if (showModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showModal]);

    // Close modal on ESC key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setShowModal(false);
                setSelectedPlan(null);
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showModal]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            case 'all':
                start = '';
                end = '';
                setSelectState('all');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
        setCurrentPage(1);
    };

    // Commit the picked draft range — this is the ONLY thing that applies a custom range
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectState('range');
        setCurrentPage(1);
    };

    const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearchInput(value);

        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setSearch(value);
            setCurrentPage(1);
        }, 500);
    };

    const handleReportSort = () => {
        setSelectState('sort');
        setSortOrder(sortOrder === 1 ? -1 : 1);
        setCurrentPage(1);
    };

    const handleStatusFilter = (status) => {
        if (statusFilter === status) {
            setStatusFilter('');
            setSelectState('today');
        } else {
            setStatusFilter(status);
            setSelectState(status);
        }
        setCurrentPage(1);
    };

    const handleSelectAll = () => {
        if (!selectAll) {
            const allIds = historyData.map((item) => item._id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectOne = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            return toast.error('Please select at least one record');
        }

        const check = generateCaptcha(`Delete ${selectedIds.length} record(s)? Solve:`);
        if (!check) return toast.error('Incorrect answer', { autoClose: 2000 });

        setLoader(true);
        try {
            const response = await Calling.deleteReferralPlanHistory({ ids: selectedIds });
            if (response?.status) {
                toast.success(response?.message || 'Records deleted successfully');
                setSelectedIds([]);
                setSelectAll(false);
                fetchHistoryData(currentPage);
            } else {
                toast.error(response?.message || 'Failed to delete');
                setLoader(false);
            }
        } catch (error) {
            toast.error('Failed to delete records');
            setLoader(false);
        }
    };

    const handleUserClick = (userId) => {
        if (userId) {
            window.open(`/manage/user/all/info/${userId}`, '_blank');
        }
    };

    const handleRowClick = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            setCurrentPage(page);
            setSelectedIds([]);
            setSelectAll(false);
            fetchHistoryData(page);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className='px-3 py-1 w-max text-xs font-medium rounded-full bg-main-th text-main-blue flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-main-blue'></span> Active</span>;
            case 'expired':
                return <span className='px-3 py-1 w-max text-xs font-medium rounded-full bg-main-yellow/10 text-main-yellow flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-main-yellow'></span> Expired</span>;
            default:
                return <span className='px-3 py-1 w-max text-xs font-medium rounded-full bg-main-bg text-main-subfont flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-main-subfont'></span> {status}</span>;
        }
    };

    // User Avatar Component
    const UserAvatar = ({ profile, name }) => {
        const getInitials = (name) => {
            if (!name) return '?';
            return name.charAt(0).toUpperCase();
        };

        if (profile) {
            return (
                <img
                    src={profile}
                    alt={name}
                    className="w-10 h-10 rounded-xl object-cover border-2 border-main-border"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                    }}
                />
            );
        }

        return (
            <div className="w-10 h-10 rounded-xl bg-main-blue flex items-center justify-center text-white font-semibold text-sm border-2 border-main-blue/30">
                {getInitials(name)}
            </div>
        );
    };

    // Stats Card Component
    const StatCard = ({ title, value, icon: Icon, bgColor, iconBgColor, textColor, borderColor, loader, onClick, isActive }) => (
        <div
            onClick={onClick}
            className={`${bgColor} rounded-xl p-4 border transition-all cursor-pointer hover:shadow-md ${isActive
                ? `${borderColor} ring-2 ring-offset-1`
                : 'border-main-border hover:border-main-blue/30'
                }`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-main-subfont">{title}</p>
                    <p className={`text-2xl font-bold mt-1 ${textColor}`}>
                        {loader ? (
                            <span className="flex items-center text-main-subfont text-base">
                                <CircularProgress size={16} sx={{ color: 'inherit', mr: 1 }} />
                                ...
                            </span>
                        ) : (
                            value.toLocaleString()
                        )}
                    </p>
                </div>
                <div className={`p-3 rounded-xl ${iconBgColor}`}>
                    <Icon size={24} className={textColor} />
                </div>
            </div>
        </div>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full min-h-screen text-main-font"
        >
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <Award size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Referral Plan History</h1>
                        <p className="text-sm text-main-subfont">Track user referral plan activations and status</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className='bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden'>
                {/* Stats Cards Grid */}
                <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <StatCard
                            title="Total Records"
                            value={stats.total}
                            icon={Award}
                            bgColor="bg-main-th"
                            iconBgColor="bg-main-exBlue/10"
                            textColor="text-main-exBlue"
                            borderColor="border-main-exBlue ring-main-exBlue/30"
                            loader={loader && currentPage === 1}
                            onClick={() => {
                                setStatusFilter('');
                                setSelectState('today');
                                setCurrentPage(1);
                            }}
                            isActive={statusFilter === ''}
                        />
                        <StatCard
                            title="Active Plans"
                            value={stats.active}
                            icon={CheckCircle2}
                            bgColor="bg-green-50"
                            iconBgColor="bg-green-100"
                            textColor="text-green-600"
                            borderColor="border-green-500 ring-green-300"
                            loader={loader && currentPage === 1}
                            onClick={() => handleStatusFilter('active')}
                            isActive={selectState === 'active'}
                        />
                        <StatCard
                            title="Expired Plans"
                            value={stats.expired}
                            icon={Clock}
                            bgColor="bg-yellow-50"
                            iconBgColor="bg-yellow-100"
                            textColor="text-yellow-600"
                            borderColor="border-yellow-500 ring-yellow-300"
                            loader={loader && currentPage === 1}
                            onClick={() => handleStatusFilter('expired')}
                            isActive={selectState === 'expired'}
                        />
                    </div>
                </div>

                {/* Date Range Filters */}
                <div className="px-4 py-4 border-t border-main-border">
                    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                        {/* Quick Range — modern segmented control */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarDays size={13} /> Quick Range
                            </p>
                            <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                {QUICK_RANGES.map((r) => {
                                    const active = selectState === r.key;
                                    return (
                                        <button
                                            key={r.key}
                                            onClick={() => handleDateRangeFilter(r.key)}
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarRange size={13} /> Custom Range
                            </p>
                            <ThemeProvider theme={pickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                        <DatePicker
                                            label="From"
                                            value={draftStart}
                                            onChange={(v) => setDraftStart(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            maxDate={draftEnd || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                            <ArrowRight size={16} />
                                        </span>
                                        <DatePicker
                                            label="To"
                                            value={draftEnd}
                                            onChange={(v) => setDraftEnd(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            minDate={draftStart || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyDateRange}
                                            sx={{ ...applyBtnSx, display: ((draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') || (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '')) ? 'inline-flex' : 'none' }}
                                            startIcon={<Check size={16} />}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                    </div>

                    {/* Applied-range indicator */}
                    {selectState === 'range' && startDate && endDate && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-main-th border border-main-blue/20 rounded-lg">
                            <CalendarRange size={14} className="text-main-blue" />
                            <span className="text-xs text-main-subfont">
                                Showing <span className="font-semibold text-main-font">{formatDate(startDate)}</span> → <span className="font-semibold text-main-font">{formatDate(endDate)}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* Action Buttons and Search */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex flex-wrap gap-2">
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <span className='flex gap-2 items-center'>
                                        {sortOrder === -1 ? <ArrowDownWideNarrow size={16} /> : <ArrowUpNarrowWide size={16} />}
                                        Sort
                                    </span>
                                }
                                selectState={selectState === 'sort'}
                            />
                        </div>

                        <div className="w-full lg:w-80">
                            <TextField
                                fullWidth
                                size="small"
                                type="search"
                                value={searchInput}
                                onChange={handleSearchInput}
                                placeholder="Search by User or Plan..."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={18} className="text-main-subfont" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className='w-full'>
                        <thead>
                            <tr className='bg-main-th'>
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            size="small"
                                            sx={checkboxSx}
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedIds.length > 0 && (
                                            <DeleteButton
                                                onClick={handleBulkDelete}
                                                label={<Trash2 size={18} />}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Plan</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Duration</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Invites</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                            </tr>
                        </thead>

                        {loader ? (
                            <tbody>
                                <tr>
                                    <td colSpan={8} className="p-0">
                                        <TableSkeleton rows={8} cols={8} />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {historyData.length > 0 ? historyData.map((item, index) => (
                                    <tr key={item._id} className='hover:bg-main-thHover cursor-pointer transition-colors'>
                                        <td className="ps-4 py-4" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                size="small"
                                                sx={checkboxSx}
                                                checked={selectedIds.includes(item._id)}
                                                onChange={() => handleSelectOne(item._id)}
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont" onClick={() => handleRowClick(item)}>
                                            {(currentPage - 1) * limit + index + 1}
                                        </td>
                                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                            <div
                                                className="flex items-center gap-3 cursor-pointer group"
                                                onClick={() => handleUserClick(item?.u_info?._id)}
                                            >
                                                <UserAvatar
                                                    profile={item?.u_info?.u_profile}
                                                    name={item?.u_info?.u_name}
                                                />
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-main-blue font-medium text-sm group-hover:underline flex items-center gap-1 truncate max-w-[150px]">
                                                        {item?.u_info?.u_name || 'N/A'}
                                                        <ExternalLink size={9} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </span>
                                                    {item?.u_info?.u_email ? (
                                                        <span className="text-xs text-main-subfont flex items-center gap-1 truncate max-w-[150px]">
                                                            <Mail size={10} className="flex-shrink-0" />
                                                            {item?.u_info?.u_email}
                                                        </span>
                                                    ) : item?.u_info?.u_device ? (
                                                        <span className="text-xs text-main-subfont truncate max-w-[150px]">
                                                            Device: {item?.u_info?.u_device?.slice(0, 12)}...
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                            <span className="font-medium text-main-font">{item.r_plan_name || 'N/A'}</span>
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-main-th text-main-blue rounded-lg text-sm font-medium border border-main-blue/20">
                                                <Clock size={12} />
                                                {item.r_plan_day || 0} Days
                                            </span>
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-main-th text-main-blue rounded-lg text-sm font-medium border border-main-blue/20">
                                                <Users size={12} />
                                                {item.u_ref_count || 0} / {item.r_plan_invite_count || 0}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                            {getStatusBadge(item.status)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont" onClick={() => handleRowClick(item)}>{formatDate(item.createdAt)}</td>
                                    </tr>
                                )) : <tr><td colSpan={8} className="py-12"><NotFound /></td></tr>}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                {!loader && historyData.length > 0 && (
                    <div className="p-6 border-t border-main-border">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="text-sm text-main-subfont">
                                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} entries
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === 1
                                        ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                        : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                        }`}
                                >
                                    <ArrowLeft size={12} /> Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === totalPages
                                        ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                        : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                        }`}
                                >
                                    Next <ArrowRight size={12} />
                                </button>
                            </div>

                            <p className="text-main-subfont text-sm">
                                Page{' '}
                                <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                    {currentPage}
                                </span>
                                {' '}of{' '}
                                <span className="font-medium text-main-font">{totalPages}</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <AnimatePresence>
                {showModal && selectedPlan && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            ref={modalRef}
                            className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden border border-main-border"
                            initial={{ scale: 0.95, y: 14, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.95, y: 14, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* Modal Header */}
                            <div className="bg-main-blue p-5 text-white relative">
                                <button
                                    onClick={() => { setShowModal(false); setSelectedPlan(null); }}
                                    className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl transition-colors"
                                >
                                    <X size={16} />
                                </button>
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-white/20 rounded-xl">
                                        <Award size={24} />
                                    </div>
                                    <div>
                                        <p className="text-white/80 text-sm">Referral Plan</p>
                                        <h2 className="text-xl font-bold">{selectedPlan?.r_plan_name || 'N/A'}</h2>
                                    </div>
                                </div>
                                <div className="mt-3">{getStatusBadge(selectedPlan?.status)}</div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
                                {/* User Card */}
                                <div
                                    onClick={() => handleUserClick(selectedPlan?.u_info?._id)}
                                    className="bg-main-th border border-main-blue/20 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {selectedPlan?.u_info?.u_profile ? (
                                                <img
                                                    src={selectedPlan?.u_info?.u_profile}
                                                    alt={selectedPlan?.u_info?.u_name}
                                                    className="w-12 h-12 rounded-xl object-cover border-2 border-main-blue/20"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-xl bg-main-blue flex items-center justify-center text-white font-semibold text-lg border-2 border-main-blue/30">
                                                    {selectedPlan?.u_info?.u_name?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-xs text-main-blue font-medium uppercase tracking-wide">User</p>
                                                <p className="font-semibold text-main-font group-hover:text-main-blue transition-colors">
                                                    {selectedPlan?.u_info?.u_name || 'N/A'}
                                                </p>
                                                {selectedPlan?.u_info?.u_email ? (
                                                    <p className="text-xs text-main-subfont mt-0.5 flex items-center gap-1">
                                                        <Mail size={10} />
                                                        {selectedPlan?.u_info?.u_email}
                                                    </p>
                                                ) : selectedPlan?.u_info?.u_device ? (
                                                    <p className="text-xs text-main-subfont mt-0.5">
                                                        Device: {selectedPlan?.u_info?.u_device}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>
                                        <ExternalLink className="text-main-blue group-hover:translate-x-1 transition-transform" size={12} />
                                    </div>
                                </div>

                                {/* Plan Details Grid */}
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                        <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Duration</p>
                                        <p className="text-xl font-bold text-main-blue mt-1">{selectedPlan?.r_plan_day || 0}</p>
                                        <p className="text-xs text-main-subfont">Days</p>
                                    </div>
                                    <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                        <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Invites</p>
                                        <p className="text-xl font-bold text-main-blue mt-1">{selectedPlan?.r_plan_invite_count || 0}</p>
                                        <p className="text-xs text-main-subfont">Allowed</p>
                                    </div>
                                    <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                        <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Referred</p>
                                        <p className="text-xl font-bold text-main-blue mt-1">{selectedPlan?.u_ref_count || 0}</p>
                                        <p className="text-xs text-main-subfont">Users</p>
                                    </div>
                                </div>

                                {/* Dates */}
                                <div className="bg-main-bg border border-main-border rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Clock className="text-main-subfont" size={14} />
                                        <p className="text-xs text-main-subfont font-medium uppercase tracking-wide">Timeline</p>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                        <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                            <p className="text-[10px] text-main-subfont uppercase">Start</p>
                                            <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.start_date)}</p>
                                        </div>
                                        <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                            <p className="text-[10px] text-main-subfont uppercase">End</p>
                                            <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.end_date)}</p>
                                        </div>
                                        <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                            <p className="text-[10px] text-main-subfont uppercase">Created</p>
                                            <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.createdAt)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-4 bg-main-bg border-t border-main-border">
                                <button
                                    onClick={() => handleUserClick(selectedPlan?.u_info?._id)}
                                    className="w-full px-4 py-2.5 bg-main-blue text-white rounded-xl hover:bg-main-blue/90 transition-colors flex items-center justify-center gap-2 font-medium"
                                >
                                    <User size={14} /> View User Profile
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default ReferralPlanHistory;
